import {
  DocumentProcessStatusEnum,
  DocumentProcessStatusFieldsEnum,
} from 'common/document/enums/document-status.enum';
import {
  DocumentVerificationUpdated,
  DocumentVerificationUpdatedMessage,
} from 'common/document/interfaces/document-verification-updated-message';
import { WebSocketClientMessageTypes } from 'contexts/webSocketClient/messageTypes';
import { useWebSocketClientSubscription } from 'contexts/webSocketClient/useWebSocketClientSubscription';
import { useTimeOut } from 'hooks/useTimeOut';
import { ESegmentCategory, track } from 'integrations/analytics';
import { useLogsManager } from 'integrations/logs';
import { useCallback, useState } from 'react';
import documentRequestCandidateService from 'store/document-request-candidate/service';

const WAITING_TIME = 20 * 1000;

export function useGetUploadLicenseStatus() {
  const logs = useLogsManager();
  const [params, setParams] = useState<{
    documentProcessStatusField: DocumentProcessStatusFieldsEnum;
    onImageProcessed?: (verification: DocumentVerificationUpdated) => void;
  }>();

  const timeOut = useTimeOut();

  const [uploadLicenseDocument, { isLoading: isUploading }] =
    documentRequestCandidateService.endpoints.uploadLicenseDocument.useMutation();

  const onImageVerificationStatusUpdated = useCallback(
    (message: DocumentVerificationUpdatedMessage) => {
      console.log('Socket-message', message);

      if (!params?.documentProcessStatusField) return;
      const documentStatus = message[params!.documentProcessStatusField];
      if (documentStatus) {
        timeOut.clearTimeOut();
      }
      params?.onImageProcessed?.(documentStatus!);
      track(ESegmentCategory.IMAGE_PROCESS_SUCCESS);
    },
    [params?.documentProcessStatusField, params?.onImageProcessed, timeOut]
  );

  useWebSocketClientSubscription(
    WebSocketClientMessageTypes.DOCUMENT_VERIFICATIONS_UPDATED,
    onImageVerificationStatusUpdated
  );

  const uploadDocument = async (
    file: File,
    url: string,
    documentProcessStatusField: DocumentProcessStatusFieldsEnum,
    onProgress?: (progress: number) => void,
    onImageUploaded?: () => void,
    onImageProcessed?: (verification: DocumentVerificationUpdated) => void
  ) => {
    setParams({
      documentProcessStatusField,
      onImageProcessed: onImageProcessed,
    });
    await uploadLicenseDocument({
      file,
      url,
      onProgress,
    });
    onImageUploaded?.();

    timeOut.setTimeOut(() => {
      track(ESegmentCategory.IMAGE_PROCESS_TIME_OUT);
      logs.logError('Image process after upload time out', {
        url,
      });
      const errorResponse = {
        updatedAt: new Date().toISOString(),
        verificationStatus: DocumentProcessStatusEnum.FAILED,
      };
      onImageProcessed?.(errorResponse!);
    }, WAITING_TIME);
  };

  return {
    isUploading,
    uploadDocument,
  };
}
